import { useState } from "react";
import type { JSX, SubmitEvent } from "react";
import { createId, type Recipe, type RecipeInput } from "../../shared/recipes";
import { formatIngredientName } from "./format-ingredient";
import { createRecipe, updateRecipe } from "../api/recipes";
import { emptyIngredient, emptyRecipeInput, emptyStep, recipeToInput, splitStep } from "./form-state";
import { RecipeTagsEditor } from "./RecipeTagsEditor";

type RecipeFormProps = {
  recipe?: Recipe;
  initialInput?: RecipeInput;
  heading?: string;
  onCancel?: () => void;
  onNavigate: (path: string) => void;
};

export function RecipeForm({ recipe, initialInput, heading, onCancel, onNavigate }: RecipeFormProps): JSX.Element {
  const [input, setInput] = useState<RecipeInput>(() => {
    if (initialInput !== undefined) {
      return initialInput;
    }
    return recipe === undefined ? emptyRecipeInput() : recipeToInput(recipe);
  });
  const [error, setError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  async function onSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setError(undefined);
    try {
      const saved = recipe === undefined
        ? await createRecipe(input)
        : await updateRecipe(recipe.id, input);
      onNavigate(`/recipes/${saved.id}`);
    } catch {
      setError("Could not save recipe. Check required fields and try again.");
      setSaving(false);
    }
  }

  return (
    <form className="recipes-panel recipe-form" onSubmit={(event) => { void onSubmit(event); }}>
      <header className="recipes-header">
        <div>
          <p className="eyebrow">Recipes</p>
          <h2>{heading ?? (recipe === undefined ? "New recipe" : "Edit recipe")}</h2>
        </div>
        {onCancel !== undefined ? (
          <button className="secondary-action" type="button" onClick={onCancel}>
            Back
          </button>
        ) : null}
      </header>

      {error !== undefined ? <p className="error-text" role="alert">{error}</p> : null}

      <label className="field">
        <span className="field-label">Title</span>
        <input
          className="text-input"
          required
          value={input.title}
          onChange={(event) => { setInput({ ...input, title: event.target.value }); }}
        />
      </label>

      <label className="field">
        <span className="field-label">Source</span>
        <input
          className="text-input"
          value={input.source ?? ""}
          placeholder="Optional"
          onChange={(event) => { setInput({ ...input, source: event.target.value }); }}
        />
      </label>

      <div className="field-row">
        <label className="field">
          <span className="field-label">Servings</span>
          <input
            className="text-input"
            type="number"
            min={1}
            required
            value={input.servings}
            onChange={(event) => { setInput({ ...input, servings: Number(event.target.value) }); }}
          />
        </label>
        <label className="field">
          <span className="field-label">Est. minutes</span>
          <input
            className="text-input"
            type="number"
            min={1}
            value={input.estimatedMinutes ?? ""}
            placeholder="Optional"
            onChange={(event) => {
              const value = event.target.value;
              setInput({
                ...input,
                estimatedMinutes: value.length === 0 ? undefined : Number(value)
              });
            }}
          />
        </label>
      </div>

      <section className="form-section">
        <h3>Tags</h3>
        <p className="muted-text">Use nested paths like Desserts &gt; Cakes. Adding a child tag also adds its ancestors.</p>
        <RecipeTagsEditor
          tags={input.tags}
          onChange={(tags) => { setInput({ ...input, tags }); }}
        />
      </section>

      <section className="form-section">
        <div className="form-section-header">
          <h3>Equipment</h3>
          <button
            type="button"
            className="text-button"
            onClick={() => { setInput({ ...input, equipment: [...input.equipment, ""] }); }}
          >
            Add
          </button>
        </div>
        {input.equipment.map((item, index) => (
          <div key={`equipment-${String(index)}`} className="equipment-row">
            <input
              className="text-input"
              value={item}
              placeholder="e.g. Large oven tray"
              onChange={(event) => {
                const equipment = [...input.equipment];
                equipment[index] = event.target.value;
                setInput({ ...input, equipment });
              }}
            />
            <button
              type="button"
              className="icon-button-small"
              aria-label="Remove equipment"
              onClick={() => {
                setInput({
                  ...input,
                  equipment: input.equipment.filter((_, equipmentIndex) => equipmentIndex !== index)
                });
              }}
            >
              ×
            </button>
          </div>
        ))}
      </section>

      <section className="form-section">
        <div className="form-section-header">
          <h3>Ingredients</h3>
          <button
            type="button"
            className="text-button"
            onClick={() => { setInput({ ...input, ingredients: [...input.ingredients, emptyIngredient()] }); }}
          >
            Add
          </button>
        </div>
        {input.ingredients.map((ingredient, index) => (
          <div key={ingredient.id} className="ingredient-row">
            <input
              className="text-input"
              value={ingredient.name}
              placeholder="Ingredient name"
              onChange={(event) => {
                const ingredients = [...input.ingredients];
                ingredients[index] = { ...ingredient, name: event.target.value };
                setInput({ ...input, ingredients });
              }}
            />
            <button
              type="button"
              className="icon-button-small"
              aria-label="Remove ingredient"
              onClick={() => {
                const ingredients = input.ingredients.filter((item) => item.id !== ingredient.id);
                const steps = input.steps.map((step) => ({
                  ...step,
                  ingredientsUsed: step.ingredientsUsed?.filter((id) => id !== ingredient.id)
                }));
                setInput({ ...input, ingredients, steps });
              }}
            >
              ×
            </button>
          </div>
        ))}
      </section>

      <section className="form-section">
        <div className="form-section-header">
          <h3>Steps</h3>
          <button
            type="button"
            className="text-button"
            onClick={() => { setInput({ ...input, steps: [...input.steps, emptyStep()] }); }}
          >
            Add step
          </button>
        </div>
        {input.steps.map((step, index) => (
          <article key={step.id} className="step-editor privacy-private">
            <div className="step-editor-header">
              <span className="step-number">{index + 1}</span>
              <select
                className="text-input step-kind"
                value={step.kind ?? "cook"}
                aria-label="Step kind"
                onChange={(event) => {
                  const steps = [...input.steps];
                  steps[index] = { ...step, kind: event.target.value as "prep" | "cook" };
                  setInput({ ...input, steps });
                }}
              >
                <option value="prep">Prep</option>
                <option value="cook">Cook</option>
              </select>
              <button
                type="button"
                className="text-button"
                onClick={() => { setInput({ ...input, steps: splitStep(input.steps, step.id) }); }}
              >
                Split
              </button>
              <button
                type="button"
                className="icon-button-small"
                aria-label="Remove step"
                disabled={input.steps.length <= 1}
                onClick={() => {
                  setInput({ ...input, steps: input.steps.filter((item) => item.id !== step.id) });
                }}
              >
                ×
              </button>
            </div>
            <label className="field">
              <span className="field-label">Summary</span>
              <input
                className="text-input"
                required
                value={step.summary}
                placeholder="Verb-led short sentence"
                onChange={(event) => {
                  const steps = [...input.steps];
                  steps[index] = { ...step, summary: event.target.value };
                  setInput({ ...input, steps });
                }}
              />
            </label>
            <label className="field">
              <span className="field-label">Details</span>
              <textarea
                className="text-input"
                rows={3}
                value={step.details ?? ""}
                placeholder="Optional longer instructions"
                onChange={(event) => {
                  const steps = [...input.steps];
                  steps[index] = { ...step, details: event.target.value };
                  setInput({ ...input, steps });
                }}
              />
            </label>
            {input.ingredients.length > 0 ? (
              <fieldset className="ingredient-checkboxes">
                <legend className="field-label">Ingredients used</legend>
                {input.ingredients.map((ingredient) => {
                  const checked = step.ingredientsUsed?.includes(ingredient.id) ?? false;
                  return (
                    <label key={ingredient.id} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          const current = new Set(step.ingredientsUsed ?? []);
                          if (event.target.checked) {
                            current.add(ingredient.id);
                          } else {
                            current.delete(ingredient.id);
                          }
                          const steps = [...input.steps];
                          steps[index] = {
                            ...step,
                            ingredientsUsed: current.size > 0 ? [...current] : undefined
                          };
                          setInput({ ...input, steps });
                        }}
                      />
                      {ingredient.name.length > 0 ? formatIngredientName(ingredient.name) : "Unnamed ingredient"}
                    </label>
                  );
                })}
              </fieldset>
            ) : null}
            <div className="timer-defs">
              {(step.timers ?? []).map((timer, timerIndex) => (
                <div key={timer.id} className="timer-def-row">
                  <input
                    className="text-input"
                    type="number"
                    min={1}
                    value={timer.durationSeconds}
                    aria-label="Timer seconds"
                    onChange={(event) => {
                      const timers = [...(step.timers ?? [])];
                      timers[timerIndex] = { ...timer, durationSeconds: Number(event.target.value) };
                      const steps = [...input.steps];
                      steps[index] = { ...step, timers };
                      setInput({ ...input, steps });
                    }}
                  />
                  <input
                    className="text-input"
                    value={timer.label ?? ""}
                    placeholder="Timer label"
                    onChange={(event) => {
                      const timers = [...(step.timers ?? [])];
                      timers[timerIndex] = { ...timer, label: event.target.value };
                      const steps = [...input.steps];
                      steps[index] = { ...step, timers };
                      setInput({ ...input, steps });
                    }}
                  />
                  <button
                    type="button"
                    className="icon-button-small"
                    aria-label="Remove timer"
                    onClick={() => {
                      const timers = (step.timers ?? []).filter((item) => item.id !== timer.id);
                      const steps = [...input.steps];
                      steps[index] = { ...step, timers: timers.length > 0 ? timers : undefined };
                      setInput({ ...input, steps });
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  const timers = [...(step.timers ?? []), { id: createId(), durationSeconds: 300 }];
                  const steps = [...input.steps];
                  steps[index] = { ...step, timers };
                  setInput({ ...input, steps });
                }}
              >
                Add timer
              </button>
            </div>
          </article>
        ))}
      </section>

      <div className="form-actions">
        <button className="secondary-action" type="button" onClick={() => { onNavigate(recipe === undefined ? "/recipes" : `/recipes/${recipe.id}`); }}>
          Cancel
        </button>
        <button className="primary-action" type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save recipe"}
        </button>
      </div>
    </form>
  );
}
